/**
 * Passkeys through Supabase Auth.
 *
 * A passkey replaces both the password and the authenticator code. Supabase
 * issues an aal1 session for a passkey sign-in, marked by a `passkey` entry in
 * the token's `amr` claim, which the app and API accept as signed in (see
 * ./mfa). Registering or removing a passkey still needs a session that
 * entered a code (aal2), which Supabase enforces.
 *
 * Passkeys are bound to the site's domain (luminaria.finance) by the
 * Supabase project's WebAuthn settings; they do not work on other origins
 * such as localhost or preview deployments.
 */

import type { PasskeyListItem, PasskeyMetadata, Session } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

export type { PasskeyListItem };

export interface PasskeySignInOptions {
  /**
   * Offer passkeys in the email field's autofill (WebAuthn Conditional UI)
   * instead of opening the browser's picker. Stays pending until the user
   * picks one or `signal` aborts.
   */
  conditional?: boolean;
  signal?: AbortSignal;
}

/** Whether this browser supports passkeys (WebAuthn) at all. */
export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

/** Whether this browser can offer passkeys through autofill on the email field. */
export async function isPasskeyAutofillAvailable(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  const credential = window.PublicKeyCredential;
  if (typeof credential.isConditionalMediationAvailable !== 'function') return false;
  try {
    return await credential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}

/** Sign in with a passkey; resolves with the (aal1, `amr` passkey) session. */
export async function signInWithPasskey(options: PasskeySignInOptions = {}): Promise<Session> {
  const { data, error } = await supabase.auth.signInWithPasskey({
    options: {
      signal: options.signal,
      ...(options.conditional ? { mediation: 'conditional' as const } : {}),
    },
  });
  if (error) throw error;
  if (!data.session) throw new Error('Passkey sign-in did not return a session.');
  return data.session;
}

/** The signed-in user's passkeys. */
export async function listPasskeys(): Promise<PasskeyListItem[]> {
  const { data, error } = await supabase.auth.passkey.list();
  if (error) throw error;
  return data;
}

/** Create a passkey on this device for the signed-in user (browser prompt). */
export async function registerPasskey(): Promise<PasskeyMetadata> {
  const { data, error } = await supabase.auth.registerPasskey();
  if (error) throw error;
  return data;
}

export async function renamePasskey(
  passkeyId: string,
  friendlyName: string
): Promise<PasskeyListItem> {
  const { data, error } = await supabase.auth.passkey.update({ passkeyId, friendlyName });
  if (error) throw error;
  return data;
}

export async function deletePasskey(passkeyId: string): Promise<void> {
  const { error } = await supabase.auth.passkey.delete({ passkeyId });
  if (error) throw error;
}
